'use client';

import * as React from 'react';

import { TooltipProvider } from '@/components/ui/tooltip';
import {
  SidebarContext,
  type SidebarCollapsible,
  type SidebarContextProps,
  type SidebarSide,
  type SidebarVariant,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_ICON,
  useSidebar,
} from '@/components/ui/sidebar-context';
import { SidebarDesktop, SidebarMobile, SidebarStatic } from '@/components/ui/sidebar-variants';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

const SIDEBAR_COOKIE_NAME = 'sidebar_state';
const SIDEBAR_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
const SIDEBAR_KEYBOARD_SHORTCUT = 'b';

interface SidebarOpenStateOptions {
  defaultOpen: boolean;
  openProp: boolean | undefined;
  setOpenProp: ((open: boolean) => void) | undefined;
}

function useSidebarOpenState({
  defaultOpen,
  openProp,
  setOpenProp,
}: Readonly<SidebarOpenStateOptions>) {
  const [_open, _setOpen] = React.useState(defaultOpen);
  const open = openProp ?? _open;
  const setOpen = React.useCallback(
    (value: boolean | ((value: boolean) => boolean)) => {
      const openState = typeof value === 'function' ? value(open) : value;
      if (setOpenProp) {
        setOpenProp(openState);
      } else {
        _setOpen(openState);
      }
      document.cookie = `${SIDEBAR_COOKIE_NAME}=${String(openState)}; path=/; max-age=${String(SIDEBAR_COOKIE_MAX_AGE)}`;
    },
    [setOpenProp, open],
  );
  return { open, setOpen };
}

function useSidebarKeyboardShortcut(toggleSidebar: () => void) {
  React.useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === SIDEBAR_KEYBOARD_SHORTCUT && (event.metaKey || event.ctrlKey)) {
        event.preventDefault();
        toggleSidebar();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleSidebar]);
}

function SidebarWrapper({
  className,
  style,
  children,
  ...props
}: Readonly<React.ComponentProps<'div'>>) {
  return (
    <TooltipProvider delayDuration={0}>
      <div
        data-slot="sidebar-wrapper"
        style={
          {
            '--sidebar-width': SIDEBAR_WIDTH,
            '--sidebar-width-icon': SIDEBAR_WIDTH_ICON,
            ...style,
          } as React.CSSProperties
        }
        className={cn(
          'group/sidebar-wrapper flex min-h-svh w-full has-data-[variant=inset]:bg-sidebar',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    </TooltipProvider>
  );
}

interface SidebarProviderProps extends React.ComponentProps<'div'> {
  defaultOpen?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function SidebarProvider({
  defaultOpen = true,
  open: openProp,
  onOpenChange: setOpenProp,
  className,
  style,
  children,
  ...props
}: Readonly<SidebarProviderProps>) {
  const isMobile = useIsMobile();
  const [openMobile, setOpenMobile] = React.useState(false);
  const { open, setOpen } = useSidebarOpenState({
    defaultOpen,
    openProp,
    setOpenProp,
  });

  const toggleSidebar = React.useCallback(() => {
    if (isMobile) {
      setOpenMobile((value) => !value);
    } else {
      setOpen((value) => !value);
    }
  }, [isMobile, setOpen, setOpenMobile]);

  useSidebarKeyboardShortcut(toggleSidebar);

  const contextValue = React.useMemo<SidebarContextProps>(
    () => ({
      state: open ? 'expanded' : 'collapsed',
      open,
      setOpen,
      isMobile,
      openMobile,
      setOpenMobile,
      toggleSidebar,
    }),
    [open, setOpen, isMobile, openMobile, setOpenMobile, toggleSidebar],
  );

  return (
    <SidebarContext.Provider value={contextValue}>
      <SidebarWrapper className={className} style={style} {...props}>
        {children}
      </SidebarWrapper>
    </SidebarContext.Provider>
  );
}

interface SidebarProps extends React.ComponentProps<'div'> {
  side?: SidebarSide;
  variant?: SidebarVariant;
  collapsible?: SidebarCollapsible;
}

export function Sidebar({
  side = 'left',
  variant = 'sidebar',
  collapsible = 'offcanvas',
  className,
  children,
  ...props
}: Readonly<SidebarProps>) {
  const { isMobile, state, openMobile, setOpenMobile } = useSidebar();

  if (collapsible === 'none') {
    return (
      <SidebarStatic className={className} {...props}>
        {children}
      </SidebarStatic>
    );
  }
  if (isMobile) {
    return (
      <SidebarMobile side={side} openMobile={openMobile} setOpenMobile={setOpenMobile} {...props}>
        {children}
      </SidebarMobile>
    );
  }
  return (
    <SidebarDesktop
      side={side}
      variant={variant}
      collapsible={collapsible}
      state={state}
      className={className}
      {...props}
    >
      {children}
    </SidebarDesktop>
  );
}
